import {
  $ppidAtom,
  emptyPpId,
  $pageViewIdAtom,
  getEmptyOrPersistedPPID
} from '@client/core/atoms/config.js';
import localStorage from '@client/core/utils/localStorage.js';
import pulse from '@finn-no/pulse-sdk';
import { debugLog } from '@schibsted-nmp/advertising-shared';

export type PulseTracker = {
  getAdvertisingIdentifiers(): Promise<PPIDResponse>;
};

export type PPIDObject = {
  ppId1?: string;
  ppId2?: string;
  adId?: string;
};

export type PPIDResponse = {
  delta?: PPIDObject;
  adform?: PPIDObject;
  xandr?: PPIDObject;
};

export type TrackerData = {
  pageViewId?: string;
};

async function getTracker(): Promise<PulseTracker> {
  const { promise, resolve, reject } = Promise.withResolvers<PulseTracker>();

  try {
    const timeout = setTimeout(() => {
      const errMsg = 'Timeout from pulse.getTracker()';
      console.warn(`[advertising-podlet] ${errMsg}`);
      reject(new Error(errMsg));
    }, 2000);

    pulse.getTracker((tracker: PulseTracker | undefined) => {
      clearTimeout(timeout);

      if (tracker) {
        resolve(tracker);
      } else {
        const errMsg = 'No tracker found from pulse.getTracker()';
        console.warn(`[advertising-podlet] ${errMsg}`);
        reject(new Error(errMsg));
      }
    });
  } catch (error) {
    if (error instanceof Error) {
      console.warn(`[advertising-podlet] ${error.message}`);
    }

    reject(error);
  }

  return promise;
}

async function getPageState(): Promise<TrackerData> {
  const { promise, resolve, reject } = Promise.withResolvers<TrackerData>();

  try {
    const timeout = setTimeout(() => {
      const errMsg = 'Timeout from pulse.getPageState()';
      console.warn(`[advertising-podlet] ${errMsg}`);
      reject(new Error(errMsg));
    }, 200);

    pulse.getPageState((pageState: TrackerData | undefined) => {
      clearTimeout(timeout);

      if (pageState) {
        resolve(pageState);
      } else {
        const errMsg = 'No page state received from pulse.getPageState()';
        console.warn(`[advertising-podlet] ${errMsg}`);
        reject(new Error(errMsg));
      }
    });
  } catch (error) {
    if (error instanceof Error) {
      console.warn(`[advertising-podlet] ${error.message}`);
    }

    reject(error);
  }

  return await promise;
}

export const PPID_STORAGE_NAME = 'advertising-podlet.ppid';

export async function persistPPID(): Promise<PPIDResponse> {
  try {
    const tracker = await getTracker();
    debugLog(`pulse tracker: ${JSON.stringify(tracker, null, 2)}`);

    const res = await getAdvertisingIdentifiers(tracker);
    debugLog(`PPID response: ${JSON.stringify(res, null, 2)}`);

    const persisted = getEmptyOrPersistedPPID();
    debugLog(`PPID persisted: ${JSON.stringify(persisted, null, 2)}`);

    const ppidData = {
      ...emptyPpId,
      ...persisted,
      ...res
    } as PPIDResponse;

    if (ppidData.delta) {
      $ppidAtom.set(ppidData);
    }

    localStorage.setItem(PPID_STORAGE_NAME, ppidData);

    return ppidData;
    // Set the object on window for use in banners.tsx and other potential places
  } catch {
    return $ppidAtom.get();
  }
}

export async function setPulsePageViewId(): Promise<string | null> {
  try {
    // Try to extract a pageViewId from pageState if it exists
    const pageViewId = await waitForPulsePageViewId();

    debugLog(`PageViewId response: ${JSON.stringify(pageViewId, null, 2)}`);

    if (pageViewId) {
      $pageViewIdAtom.set(pageViewId);
    }

    return pageViewId ?? $pageViewIdAtom.get();
  } catch {
    return $pageViewIdAtom.get();
  }
}

export async function getAdvertisingIdentifiers(tracker: PulseTracker) {
  // gets delta and xandr automatically
  return await tracker.getAdvertisingIdentifiers();
}

async function waitForPulsePageViewId({
  maxAttempts = 3,
  interval = 100
} = {}): Promise<string | null> {
  const startTime = Date.now();

  for (let i = 0; i < maxAttempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const pageState = await getPageState().catch(() => null);
    const pageViewId = pageState?.pageViewId;

    if (pageViewId) {
      const elapsedTime = Date.now() - startTime;

      debugLog(
        `pageViewId resolved after ${i + 1} attempts and ${elapsedTime}ms`
      );

      return pageViewId;
    }

    const { promise, resolve } = Promise.withResolvers<void>();

    setTimeout(resolve, interval);
    await promise; // eslint-disable-line no-await-in-loop
  }

  console.warn('pageViewId not resolved in time');

  return null;
}
